/* eslint-disable import/no-webpack-loader-syntax */
/**
 * assetManifest
 *
 * Builds the garment + pattern catalogue straight from the files in ./assets.
 * Nothing here talks to an API.
 *
 * Garments are discovered by folder convention (every file is 1650x1550):
 *
 *   assets/garments/<slug>/background-<slug>.png   base garment (transparent bg)
 *   assets/garments/<slug>/multiply-<slug>.png     shading, multiply-blended on top
 *   assets/garments/<slug>/clipmask-<slug>.svg     <path>s that bound the patterns
 *   assets/garments/<slug>/outline-<slug>.svg      stroke outline, drawn last
 *   assets/garments/<slug>/stripes/<name>.svg      each file is one stripe option
 *
 * Add a garment or a stripe by dropping files in the folder - no code changes.
 *
 * Patterns are listed explicitly below. They are imported with `!!file-loader!`
 * because CRA otherwise pipes every JS-imported .svg through SVGR, which would
 * compile multi-megabyte pattern files (plaid.svg is 7.5 MB) into JS modules.
 * The `!!` prefix skips SVGR and gives us the plain emitted file URL.
 */
import camoUrl from '!!file-loader?name=static/media/[name].[hash].[ext]!./assets/patterns/camo.svg';
import circutUrl from '!!file-loader?name=static/media/[name].[hash].[ext]!./assets/patterns/circut.svg';
import cubesUrl from '!!file-loader?name=static/media/[name].[hash].[ext]!./assets/patterns/cubes.svg';
import plaidUrl from '!!file-loader?name=static/media/[name].[hash].[ext]!./assets/patterns/plaid.svg';
import trianglesUrl from '!!file-loader?name=static/media/[name].[hash].[ext]!./assets/patterns/triangles.svg';
import digitalCamoUrl from '!!file-loader?name=static/media/[name].[hash].[ext]!./assets/patterns/digital-camo.svg';
import houndstoothUrl from '!!file-loader?name=static/media/[name].[hash].[ext]!./assets/patterns/houndstooth.svg';
import usaFlagUrl from '!!file-loader?name=static/media/[name].[hash].[ext]!./assets/patterns/USA-flag.svg';
import splatterUrl from '!!file-loader?name=static/media/[name].[hash].[ext]!./assets/patterns/splatter.svg';
import pinStripesUrl from '!!file-loader?name=static/media/[name].[hash].[ext]!./assets/patterns/pin-stripes.svg';
import horizontalStripesUrl from '!!file-loader?name=static/media/[name].[hash].[ext]!./assets/patterns/horizontal-stripes.svg';

const titleCase = (s) =>
    s.split('-').filter(Boolean).map((w) => w[0].toUpperCase() + w.slice(1)).join(' ');

// Webpack returns either the URL string or a module namespace with `.default`.
const urlOf = (m) => (m && m.default) || m;

// ── Garments ────────────────────────────────────────────────────────────────
const garmentFiles = require.context('./assets/garments', true, /\.(png|svg)$/);

const KEY_RE = /^\.\/([^/]+)\/(?:(background|multiply|clipmask|outline)-[^/]+|stripes\/([^/]+)\.svg)$/;

const bySlug = {};
garmentFiles.keys().forEach((key) => {
    const m = key.match(KEY_RE);
    if (!m) return;
    const [, slug, role, stripeStem] = m;
    const g = bySlug[slug] || (bySlug[slug] = {
        slug,
        name: titleCase(slug),
        background: null,
        multiply: null,
        clipmask: null,
        outline: null,
        stripes: [],
    });
    const url = urlOf(garmentFiles(key));
    if (role) {
        g[role] = url;
    } else {
        g.stripes.push({
            id: stripeStem,
            label: titleCase(stripeStem.replace(/^.*?stripe-/, '')),
            url,
        });
    }
});

export const GARMENTS = Object.values(bySlug)
    .filter((g) => g.background && g.clipmask)
    .map((g) => ({ ...g, stripes: g.stripes.sort((a, b) => a.label.localeCompare(b.label)) }))
    .sort((a, b) => a.slug.localeCompare(b.slug));

// ── Patterns ────────────────────────────────────────────────────────────────
// A flat single-colour fill so the garment body (or a stripe) can be a plain
// colour. Built in code, there is no file for it.
const SOLID_SVG =
    '<svg xmlns="http://www.w3.org/2000/svg" width="1650" height="1550" viewBox="0 0 1650 1550">' +
    '<style>.cls-1{fill:#1d428a;}</style>' +
    '<rect class="cls-1" width="1650" height="1550"/></svg>';

export const SOLID_PATTERN_ID = 'solid';

export const PATTERNS = [
    {
        id: SOLID_PATTERN_ID,
        label: 'Solid',
        url: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(SOLID_SVG)}`,
    },
    { id: 'camo', label: 'Camo', url: urlOf(camoUrl) },
    { id: 'circut', label: 'Circuit', url: urlOf(circutUrl) },
    { id: 'cubes', label: 'Cubes', url: urlOf(cubesUrl) },
    { id: 'plaid', label: 'Plaid', url: urlOf(plaidUrl) },
    { id: 'triangles', label: 'Triangles', url: urlOf(trianglesUrl) },
    { id: 'digital-camo', label: 'Digital Camo', url: urlOf(digitalCamoUrl) },
    { id: 'houndstooth', label: 'Houndstooth', url: urlOf(houndstoothUrl) },
    { id: 'usa-flag', label: 'USA Flag', url: urlOf(usaFlagUrl) },
    { id: 'splatter', label: 'Splatter', url: urlOf(splatterUrl) },
    { id: 'pin-stripes', label: 'Pin Stripes', url: urlOf(pinStripesUrl) },
    { id: 'horizontal-stripes', label: 'Horizontal Stripes', url: urlOf(horizontalStripesUrl) },
];
